import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAppConfig } from "./useAppConfig";

export interface ArtistImage {
  /** Display name: the matched Spotify name, or the lineup text for B2B parts. */
  name: string;
  url: string;
  spotifyUrl: string | null;
}

/** Whether the Spotify artist-images feature is on (admin kill-switch). */
export function useSpotifyImagesEnabled(): boolean {
  const { data } = useAppConfig<{ enabled: boolean }>("spotify_artist_images", {
    enabled: false,
  });
  return data?.enabled === true;
}

interface ArtistEntry {
  name: string;
  image: { url: string; spotifyUrl: string | null } | null;
}

interface FetchArtistImageResponse {
  enabled: boolean;
  artists: ArtistEntry[];
}

export const ARTIST_IMAGE_STALE_TIME = 24 * 60 * 60 * 1000;

/** Stable React Query key, shared by the live hook and the page prefetch. */
export const artistImageQueryKey = (name: string) => [
  "artist-image",
  (name ?? "").trim().toLowerCase(),
];

/**
 * The one definition of an artist-photo query: key, fetcher and cache
 * lifetime. Both the popup and the page prefetch use it, so a prefetched
 * photo is guaranteed to be the entry the popup reads.
 *
 * gcTime is pinned alongside staleTime: a prefetched entry has no observer
 * until someone opens a photo, and the app-wide 10-minute default would evict
 * it long before the day-long staleTime this query claims.
 */
export const artistImageQueryOptions = (name: string) => ({
  queryKey: artistImageQueryKey(name),
  queryFn: () => fetchArtistImages(name),
  staleTime: ARTIST_IMAGE_STALE_TIME,
  gcTime: ARTIST_IMAGE_STALE_TIME,
});

/** Resolve Spotify photos for a lineup entry (one per artist; B2B → several). */
export async function fetchArtistImages(name: string): Promise<ArtistImage[]> {
  const trimmed = (name ?? "").trim();
  if (trimmed.length < 2) return [];

  const { data, error } = await supabase.functions.invoke<FetchArtistImageResponse>(
    "fetch-artist-image",
    { body: { artistName: trimmed } },
  );
  // Throw so React Query keeps the last good result and retries, rather than
  // overwriting a cached photo with an empty list on a transient failure.
  if (error) throw error;

  return (data?.artists ?? [])
    .filter((a): a is ArtistEntry & { image: NonNullable<ArtistEntry["image"]> } => !!a.image)
    .map((a) => ({ name: a.name, url: a.image.url, spotifyUrl: a.image.spotifyUrl }));
}

/**
 * Resolve Spotify promo photos for a lineup entry by name.
 *
 * Returns one image per performing artist: usually one, but B2B / vs sets
 * ("Adam Beyer B2B Cirez D") return one per artist. Gated on the
 * `spotify_artist_images` admin toggle. Entries without a confident match are
 * dropped, so the array only ever contains real artist photos.
 */
export function useArtistImages(artistName: string | null | undefined): {
  artists: ArtistImage[];
  isLoading: boolean;
  featureEnabled: boolean;
} {
  const featureEnabled = useSpotifyImagesEnabled();
  const name = (artistName ?? "").trim();

  const { data, isLoading } = useQuery<ArtistImage[]>({
    ...artistImageQueryOptions(name),
    enabled: featureEnabled && name.length > 1,
    retry: false,
  });

  return {
    // A disabled query (feature off / no name) already reports isLoading=false.
    artists: data ?? [],
    isLoading,
    featureEnabled,
  };
}
